import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    publishableKey: String,
    paymentIntentUrl: String,
    returnUrl: String,
    hasRecurring: Boolean,
    connectedAccount: String
  }

  async connect() {
    // Load Stripe.js
    if (!window.Stripe) {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.async = true
      script.onload = () => this.initializeStripe()
      document.head.appendChild(script)
    } else {
      this.initializeStripe()
    }
  }

  get brandColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-500').trim() || '#3b82f6'
  }

  async initializeStripe() {
    // Initialize Stripe with connected account for direct charges
    const options = {}
    if (this.connectedAccountValue) {
      options.stripeAccount = this.connectedAccountValue
    }
    this.stripe = Stripe(this.publishableKeyValue, options)

    // Create PaymentIntent or SetupIntent on the server
    const response = await fetch(this.paymentIntentUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
      }
    })

    const { client_secret, error } = await response.json()

    if (error) {
      this.showError(error)
      return
    }

    // Initialize Payment Element
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: this.brandColor,
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px'
      }
    }

    this.elements = this.stripe.elements({
      clientSecret: client_secret,
      appearance: appearance
    })

    const paymentElement = this.elements.create('payment')
    paymentElement.mount('#payment-element')

    // Handle validation errors
    paymentElement.on('change', (event) => {
      if (event.error) {
        this.showError(event.error.message)
      } else {
        this.hideError()
      }
    })
  }

  async submit(event) {
    event.preventDefault()

    if (!this.stripe || !this.elements) {
      return
    }

    this.setLoading(true)

    // Use confirmSetup for recurring payments, confirmPayment for one-time
    if (this.hasRecurringValue) {
      const { error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrlValue
        }
      })

      if (error) {
        this.showError(error.message)
        this.setLoading(false)
      }
    } else {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrlValue
        }
      })

      if (error) {
        this.showError(error.message)
        this.setLoading(false)
      }
    }
  }

  setLoading(isLoading) {
    const buttonText = document.getElementById('button-text')
    const buttonSpinner = document.getElementById('button-spinner')
    const submitButton = document.getElementById('submit-button')

    if (isLoading) {
      submitButton.disabled = true
      buttonText.classList.add('hidden')
      buttonSpinner.classList.remove('hidden')
    } else {
      submitButton.disabled = false
      buttonText.classList.remove('hidden')
      buttonSpinner.classList.add('hidden')
    }
  }

  showError(message) {
    const errorElement = document.getElementById('payment-errors')
    errorElement.textContent = message
    errorElement.classList.remove('hidden')
  }

  hideError() {
    const errorElement = document.getElementById('payment-errors')
    errorElement.classList.add('hidden')
  }
}
